import React, { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { ClipboardList, Scale, Sparkles, AlertTriangle, Loader2 } from 'lucide-react';
import KpiCard from '../../components/KpiCard';
import { computeRecepcionIndicadores } from '../../helpers/registrosIndicadores';
import { ChartBox, EmptyChart, HorizontalBars, COLORS, fmtNum, dayAxisProps } from './chartParts';

const fmtKg = (n) => `${fmtNum(n, 1)} kg`;

// Indicadores y gráficas de la recepción de ropa sucia del periodo.
const RecepcionIndicadoresSection = ({ registros }) => {
  const { data, loading, error } = registros;
  const ind = useMemo(() => computeRecepcionIndicadores(data), [data]);
  const k = ind.kpis;

  if (error) {
    return <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm rounded-xl p-4">{error}</div>;
  }
  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Calculando indicadores…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={<ClipboardList className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Lotes recibidos"
          value={fmtNum(k.lotes)}
          unit="lotes"
          sub={{ label: 'Finalizados / Pendientes', value: `${fmtNum(k.finalizados)} / ${fmtNum(k.pendientes)}` }}
          sub2={{ label: 'Promedio por lote', value: fmtKg(k.promKgLote) }}
        />
        <KpiCard
          icon={<Scale className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Kilos recibidos"
          value={fmtNum(k.kilos, 1)}
          unit="kg"
          sub={{ label: 'Kilos por día', value: fmtKg(k.kgPorDia) }}
          sub2={{ label: 'Días con recepción', value: fmtNum(k.diasConDatos) }}
        />
        <KpiCard
          icon={<Sparkles className="h-3.5 w-3.5 text-[#0E9F6E]" />}
          label="Kilos limpios"
          value={fmtNum(k.kilosLimpios, 1)}
          unit="kg"
          sub={{ label: 'De lotes finalizados', value: fmtNum(k.finalizados) }}
        />
        <KpiCard
          icon={<AlertTriangle className="h-3.5 w-3.5 text-[#B71D3F]" />}
          label="Rechazo"
          value={fmtNum(k.pctRechazo, 2)}
          unit="%"
          status={k.kilosRechazo > 0 ? 'danger' : undefined}
          showTag={false}
          sub={{ label: 'Kilos rechazados', value: fmtKg(k.kilosRechazo) }}
          sub2={{ label: 'Sobre kilos finalizados', value: fmtKg(k.kilosFinalizados) }}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartBox title="Kilos y lotes por día" hint="Barras: kilos recibidos · Línea: cantidad de lotes.">
          {ind.porDia.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer>
              <ComposedChart data={ind.porDia} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis {...dayAxisProps(ind.porDia.length)} />
                <YAxis yAxisId="kg" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="lotes" orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip labelFormatter={(l) => `Día ${l}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="kg" dataKey="kilos" name="Kilos" fill={COLORS.navy} radius={[3, 3, 0, 0]} />
                <Line yAxisId="lotes" type="monotone" dataKey="lotes" name="Lotes" stroke={COLORS.coral} strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </ChartBox>

        <ChartBox title="Top clientes por kilos" hint="Los 10 clientes con más kilos recibidos.">
          <HorizontalBars data={ind.porCliente} dataKey="kilos" name="Kilos" unit=" kg" decimals={1} />
        </ChartBox>

        <ChartBox title="Kilos por tipo de prenda" hint="Los 10 tipos de prenda con más kilos recibidos.">
          <HorizontalBars data={ind.porTipoPrenda} dataKey="kilos" name="Kilos" color={COLORS.lightBlue} unit=" kg" decimals={1} />
        </ChartBox>

        <ChartBox title="Rechazo por tipo" hint="Kilos rechazados al finalizar el lavado, por causa.">
          {ind.rechazoPorTipo.length === 0 ? <EmptyChart text="Sin rechazos registrados en el periodo." /> : (
            <ResponsiveContainer>
              <BarChart data={ind.rechazoPorTipo} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="nombre" tick={{ fontSize: 11 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtKg(v)} />
                <Bar dataKey="kilos" name="Kilos rechazados" fill={COLORS.coral} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartBox>
      </div>
    </div>
  );
};

export default RecepcionIndicadoresSection;
